import {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { makeStyles } from '@material-ui/core';
import { useSidebarOpenState } from '@backstage/core-components';
import { useLocation } from 'react-router-dom';

/**
 * The head of the navigation column, as the public portals draw it (DX-352):
 * a title row above a hairline, then a field that filters the entries under
 * it.
 *
 * Ported from the Developer Portal's `DocsSidebarNav.vue` and its
 * `.docs-sidebar-header`, `-title` and `-filter` rules, which are in turn the
 * Cockpit's shelf. Help draws the same column. The values are theirs; the
 * behaviour differs in one place, on purpose: the portals filter a tree whose
 * matches hide inside collapsed parents, and flatten the result into one
 * list. This column is short and one level deep, so each entry simply hides
 * when it does not match, and stays under the group it belongs to.
 *
 * MUI v4 `makeStyles` only — no MUI v5.
 */

type ShelfFilter = {
  query: string;
  setQuery: (query: string) => void;
  /** Whether an entry with this text stays visible under the current query. */
  matches: (...texts: (string | undefined)[]) => boolean;
  filtering: boolean;
};

const NO_FILTER: ShelfFilter = {
  query: '',
  setQuery: () => {},
  matches: () => true,
  filtering: false,
};

const ShelfFilterContext = createContext<ShelfFilter>(NO_FILTER);

/**
 * The filter as the entries should apply it: only while the column is open.
 * Collapsed to its icons the field is not drawn, and a query nobody can see —
 * or clear — must not go on hiding entries, so the column shows all of them
 * and the query waits for the column to open again.
 */
export const useShelfFilter = () => {
  const filter = useContext(ShelfFilterContext);
  const { isOpen } = useSidebarOpenState();
  return isOpen ? filter : NO_FILTER;
};

export const ShelfFilterProvider = ({ children }: PropsWithChildren<{}>) => {
  const [query, setQuery] = useState('');
  // A query is for finding the next page, not a setting: once the reader has
  // gone somewhere, the column is whole again.
  const { pathname } = useLocation();
  useEffect(() => setQuery(''), [pathname]);
  const value = useMemo(() => {
    const q = query.trim().toLowerCase();
    return {
      query,
      setQuery,
      filtering: q.length > 0,
      matches: (...texts: (string | undefined)[]) =>
        !q || texts.some(text => text?.toLowerCase().includes(q)),
    };
  }, [query]);
  return (
    <ShelfFilterContext.Provider value={value}>
      {children}
    </ShelfFilterContext.Provider>
  );
};

/**
 * Marks a visible entry, so `ShelfNoMatches` can tell whether any is left.
 * Every row the filter can hide carries it.
 */
export const SHELF_ROW = { 'data-shelf-row': '' };

const useStyles = makeStyles({
  /*
    Sticky, so the title and the field stay put while the entries scroll under
    them — the portals keep them outside their scrolling list; here the whole
    drawer is the scroll container, and sticking is the way to the same thing.
    Opaque for the same reason: the entries would read through it.
  */
  head: {
    position: 'sticky',
    top: 0,
    zIndex: 1,
    width: '100%',
    background: 'inherit',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    // `--docs-shelf-header-height` on the portals: the height their
    // breadcrumb bar shares, so the two dividers meet.
    height: '3.5rem',
    padding: '0 1rem',
    borderBottom: '1px solid var(--ui-nav-rule)',
    boxSizing: 'border-box',
  },
  title: {
    margin: 0,
    fontSize: '1.125rem',
    fontWeight: 500,
    lineHeight: 1.4,
    color: 'var(--ui-nav-title)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  filter: {
    padding: '0.75rem 0.5rem 0.5rem',
  },
  input: {
    width: '100%',
    boxSizing: 'border-box',
    padding: '0.375rem 0.625rem',
    border: '1px solid var(--ui-nav-filter-border)',
    borderRadius: '0.375rem',
    background: 'var(--ui-nav-filter-bg)',
    color: 'var(--ui-nav-filter-text)',
    font: 'inherit',
    fontSize: '0.8125rem',
    lineHeight: 1.4,
    outline: 'none',
    transition: 'border-color 160ms ease, background-color 160ms ease',
    '&::placeholder': {
      color: 'var(--ui-nav-filter-placeholder)',
    },
    '&:focus': {
      borderColor: 'var(--color-brand-500)',
      background: 'var(--ui-nav-filter-bg-focus)',
    },
  },
  /*
    Shown while a query hides every entry. It sits after all of them and is
    hidden by any visible row before it — a sibling selector rather than a
    count, because the rows are drawn by five components and none of them
    knows about the others.
  */
  noMatches: {
    margin: '0.75rem 0 0',
    padding: '0 1rem',
    fontSize: '0.8125rem',
    color: 'var(--ui-nav-label)',
    '[data-shelf-row] ~ &': {
      display: 'none',
    },
  },
});

/**
 * The title and the filter.
 *
 * The title is the section of the bar the reader is in — "Catalog", "Docs" —
 * which is what the portals print there, and falls back to the product's name
 * on a page that belongs to no section.
 *
 * Collapsed to its icons, the column has no room for either, and draws
 * neither; hovering it open brings both back.
 */
export const ShelfHeader = ({ title }: { title: string }) => {
  const classes = useStyles();
  const { isOpen } = useSidebarOpenState();
  const { query, setQuery } = useContext(ShelfFilterContext);

  if (!isOpen) {
    return null;
  }

  return (
    <div className={classes.head} data-shelf-head="">
      <div className={classes.header}>
        {/* A paragraph, not a heading: the column's headings are its group
            names (AC-5), and a reader navigating by them should meet Main
            first, not a repeat of the bar's section. */}
        <p className={classes.title}>{title}</p>
      </div>
      <div className={classes.filter}>
        <input
          className={classes.input}
          type="search"
          placeholder="Filter"
          aria-label="Filter the navigation"
          spellCheck={false}
          autoComplete="off"
          value={query}
          onChange={e => setQuery(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Escape') setQuery('');
          }}
        />
      </div>
    </div>
  );
};

export const ShelfNoMatches = () => {
  const classes = useStyles();
  const { filtering } = useShelfFilter();
  if (!filtering) {
    return null;
  }
  return <p className={classes.noMatches}>No matches</p>;
};
